import styled, { keyframes } from "styled-components";
import { windowWidth } from "@styles/global";
import { ModuleState } from "./types";

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const LauncherWrap = styled.div`
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const LauncherDeco = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  display: none;

  @media (min-width: ${windowWidth.tablet}) {
    display: block;
  }
`;

export const DecoNavy = styled.div`
  position: absolute;
  inset: 0;
  background: ${(props) => props.theme.brand.navy};
  clip-path: polygon(64% 0, 100% 0, 100% 100%, 46% 100%);
`;

export const DecoStripeW = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.055);
  clip-path: polygon(30% 0, 33.5% 0, 11% 100%, 7.5% 100%);
`;

export const DecoStripeG = styled.div`
  position: absolute;
  inset: 0;
  background: ${(props) => props.theme.brand.gold};
  opacity: 0.2;
  clip-path: polygon(20% 0, 23% 0, 1% 100%, -2% 100%);
`;

export const LauncherScroll = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 20px;

  @media (min-width: ${windowWidth.tablet}) {
    padding: 30px 40px;
    /* "safe center" centers when there is room and falls back to flex-start
       when the content is taller than the viewport — without it, centered
       overflow pushes the top out of scroll range and clips it at 100% zoom. */
    align-items: safe center;
  }
`;

export const LauncherInner = styled.div`
  width: 100%;
  max-width: 1100px;
`;

export const LauncherHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

export const Eyebrow = styled.p`
  font-size: 12px;
  color: ${(props) => props.theme.brand.soft};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

export const Greeting = styled.h1`
  font-weight: 700;
  font-size: 25px;
  color: ${(props) => props.theme.brand.navy};
  letter-spacing: -0.01em;
`;

export const ActivePill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${(props) => props.theme.brand.soft};
`;

export const ActiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.theme.brand.green};
  display: inline-block;
`;

export const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: ${windowWidth.mobile.large}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: ${windowWidth.laptop.medium}) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: ${windowWidth.laptop.large}) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

interface MCardProps {
  $state: ModuleState;
  $accent: string;
  $index: number;
}

// 3-state visual rules:
//   - accessible:              full opacity, hover lifts the card.
//   - available-no-access:     dimmed + "not-allowed" cursor; hover is a no-op.
//   - locked-not-contracted:   dimmed + pointer cursor; hover lifts (the user
//                              still gets a CTA: the upsell modal).
export const MCard = styled.div<MCardProps>`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 53, 99, 0.1);
  cursor: ${(props) => (props.$state === "available-no-access" ? "not-allowed" : "pointer")};
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  padding: 22px 18px 20px;
  min-height: 144px;
  opacity: ${(props) => (props.$state === "accessible" ? 1 : 0.78)};
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  animation: ${cardIn} 0.35s ease both;
  animation-delay: ${(props) => props.$index * 35 + 20}ms;

  &:hover {
    ${(props) => props.$state === "accessible" && `
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0, 53, 99, 0.18);
      border-color: ${props.$accent};
    `}
    ${(props) => props.$state === "locked-not-contracted" && `
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0, 53, 99, 0.18);
    `}
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.001s;
    animation-delay: 0s;
  }
`;

export const MCardShine = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.14) 0%, transparent 60%);
`;

interface MLabelProps {
  $locked: boolean;
}

export const MLabel = styled.p<MLabelProps>`
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.06em;
  line-height: 1;
  color: ${(props) => (props.$locked ? "#bbb" : props.theme.brand.navy)};
`;

export const LockBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #eef1f4;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa4b2;
  font-size: 12px;
`;

export const LicenseBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #fff7e6;
  border: 1px solid ${(props) => props.theme.brand.gold};
  border-left: 4px solid ${(props) => props.theme.brand.gold};

  span {
    font-size: 16px;
    line-height: 1;
  }

  p {
    font-size: 13px;
    line-height: 1.5;
    color: ${(props) => props.theme.brand.ink};
  }
`;

export const LicenseBlock = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 20, 50, 0.62);
  backdrop-filter: blur(6px);
`;

export const LicenseBlockCard = styled.div`
  width: 420px;
  max-width: 92vw;
  background: #fff;
  border-radius: 16px;
  padding: 32px 30px;
  text-align: center;
  box-shadow: 0 24px 80px rgba(0, 30, 70, 0.3);

  .license-block-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    background: #fff1e0;
  }

  h2 {
    font-size: 19px;
    font-weight: 700;
    color: ${(props) => props.theme.brand.navy};
    margin-bottom: 8px;
  }

  p {
    font-size: 13px;
    line-height: 1.6;
    color: ${(props) => props.theme.brand.muted};
  }
`;

export const StateMessage = styled.div`
  padding: 56px 20px;
  text-align: center;
  font-size: 14px;
  color: ${(props) => props.theme.brand.muted};
`;

const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

// Loading placeholder with the exact footprint of an MCard, so the grid does
// not reflow when the real cards replace the skeletons.
export const SkeletonCard = styled.div`
  border-radius: 10px;
  min-height: 144px;
  background: #e2e7ee;
  animation: ${skeletonPulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Layout for a grouped-card menu item (FMX suite → FMX / DRIVER). Renders the
// child label (bold) above its short description (secondary), with an optional
// lock affordance on the right of the label row.
export const GroupMenuItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 220px;
`;

export const GroupMenuItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const GroupMenuItemLabel = styled.span`
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.04em;
  color: ${(props) => props.theme.brand.navy};
`;

export const GroupMenuItemLock = styled.span`
  font-size: 12px;
  line-height: 1;
  color: #9aa4b2;
`;

export const GroupMenuItemDesc = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${(props) => props.theme.brand.muted};
  white-space: normal;
`;
